import { UnitHistory } from '../unit-specific-facts';

type TemperatureFacts = {
  [key: string]: UnitHistory;
};

export const temperatureFacts: TemperatureFacts = {
  "celsius": {
    name: "Celsius",
    shortDescription: "The most widely used temperature scale worldwide",
    history: "In 1742, Anders Celsius presented his revolutionary temperature scale to the Swedish Academy of Sciences. Fascinatingly, his original scale ran backward from today's version - he marked boiling water as 0° and freezing water as 100°. The scale was reversed after his death, likely by botanist Carl Linnaeus, who found it more intuitive for studying plant growth.",
    origin: "Named after Swedish astronomer Anders Celsius, whose meticulous experiments with mercury thermometers laid the groundwork for modern temperature measurement",
    modernUsage: "Now the standard temperature scale in most countries worldwide, the Celsius scale's elegant water-based reference points have made it invaluable in both scientific research and everyday life",
    funFacts: [
      "The original Celsius scale marked boiling point as 0° and freezing as 100° - the exact opposite of today's scale",
      "Mercury's uniform thermal expansion made early Celsius thermometers remarkably accurate, varying by less than 0.1°C",
      "Celsius was also an astronomer who studied the aurora borealis and helped determine the exact shape of the Earth"
    ],
    related: ["kelvin", "fahrenheit"],
    images: []
  },
  "fahrenheit": {
    name: "Fahrenheit",
    shortDescription: "Temperature scale commonly used in the United States",
    history: "Daniel Gabriel Fahrenheit developed his scale in 1724, using a mixture of ice, water, and ammonium chloride to establish 0°F. He set human body temperature as 96°F (later adjusted to 98.6°F), and water's freezing and boiling points became 32°F and 212°F respectively.",
    origin: "Named after German physicist Daniel Gabriel Fahrenheit (1686-1736)",
    modernUsage: "Primary temperature scale in the United States and its territories, also used in some Caribbean nations",
    funFacts: [
      "Based on three fixed temperature points",
      "180 degrees between freezing and boiling of water",
      "Originally calibrated to human body temperature"
    ],
    related: ["celsius", "rankine"],
    images: []
  },
  "kelvin": {
    name: "Kelvin",
    shortDescription: "The SI base unit of temperature",
    history: "William Thomson (Lord Kelvin) proposed the absolute temperature scale in 1848. He determined that -273.15°C was the lowest possible temperature (absolute zero). The Kelvin scale starts at this point, making negative temperatures impossible.",
    origin: "Named after British physicist William Thomson, 1st Baron Kelvin (1824-1907)",
    modernUsage: "Used primarily in scientific work, especially in physics and chemistry, where absolute temperature values are needed",
    funFacts: [
      "Has no degrees symbol - just 'K'",
      "Zero Kelvin is absolute zero",
      "Used in all fundamental physical laws"
    ],
    related: ["celsius", "rankine"],
    images: []
  },
  "reaumur": {
    name: "Réaumur",
    shortDescription: "A historical temperature scale once popular in Europe",
    history: "René Antoine Ferchault de Réaumur introduced his scale in 1730. He set 0° as water's freezing point and 80° as its boiling point. The scale was widely used in Europe, particularly in France, Germany, and Russia, during the 18th and 19th centuries.",
    origin: "Named after French scientist René Antoine Ferchault de Réaumur (1683-1757)",
    modernUsage: "Largely obsolete but occasionally found on antique thermometers and in historical scientific literature",
    funFacts: [
      "Used 80 degrees between freezing and boiling",
      "Popular in wine-making regions historically",
      "Still used in some cheese making applications"
    ],
    related: ["celsius", "fahrenheit"],
    images: []
  },
  "rankine": {
    name: "Rankine",
    shortDescription: "An absolute temperature scale based on Fahrenheit degrees",
    history: "William John Macquorn Rankine developed this absolute temperature scale in 1859. The Rankine scale uses Fahrenheit degrees but starts at absolute zero, similar to how Kelvin relates to Celsius. It was particularly useful in English-speaking engineering applications.",
    origin: "Named after Scottish engineer William John Macquorn Rankine (1820-1872)",
    modernUsage: "Primarily used in some engineering systems, particularly in the United States where Fahrenheit is common",
    funFacts: [
      "Zero Rankine is absolute zero",
      "Used in thermodynamic calculations",
      "459.67°R equals 0°F"
    ],
    related: ["fahrenheit", "kelvin"],
    images: []
  }
};
